/**
 * Canonical decision-type label helpers.
 *
 * Two entry points:
 *  - `getDecisionTypeLabel`       — React component context (has `useTranslations`)
 *  - `getDecisionTypeLabelStatic` — non-hook context (Redux reducers / action creators)
 *
 * The static maps below must stay in sync with `messages/sk.json` and
 * `messages/en.json` under `Filters.decisionTypeLabels.*`.
 */

export const KNOWN_DECISION_TYPE_KEYS = new Set([
  'sudny-dvor-eu',
  'europsky-sud-lp',
  'judikaty',
  'vseobecne-sudy-sr',
  'ustavny-sud-sr',
]);

export const DECISION_TYPE_LABELS_STATIC_SK: Record<string, string> = {
  'sudny-dvor-eu': 'Súdny dvor EÚ',
  'europsky-sud-lp': 'Európsky súd pre ľudské práva',
  judikaty: 'Judikáty',
  'vseobecne-sudy-sr': 'Všeobecné súdy SR',
  'ustavny-sud-sr': 'Ústavný súd SR',
};

export const DECISION_TYPE_LABELS_STATIC_EN: Record<string, string> = {
  'sudny-dvor-eu': 'Court of Justice of the EU',
  'europsky-sud-lp': 'European Court of Human Rights',
  judikaty: 'Published case law',
  'vseobecne-sudy-sr': 'Slovak general courts',
  'ustavny-sud-sr': 'Constitutional Court of the Slovak Republic',
};

/**
 * React-component context.
 *
 * Pass the `t` function from `useTranslations('Filters')`:
 * ```ts
 * const tFilters = useTranslations('Filters');
 * const label = getDecisionTypeLabel(typeId, tFilters);
 * ```
 *
 * Falls back to `typeId` for unknown keys so new types degrade gracefully.
 */
export function getDecisionTypeLabel(
  typeId: string,
  t: (key: string) => string
): string {
  if (!KNOWN_DECISION_TYPE_KEYS.has(typeId)) return typeId;
  return t(`decisionTypeLabels.${typeId}`);
}

/**
 * Non-hook context (Redux reducers, action creators, thunks).
 *
 * Uses the hardcoded static map. Defaults to Slovak (app default locale).
 */
export function getDecisionTypeLabelStatic(
  typeId: string,
  locale: 'sk' | 'en' = 'sk'
): string {
  const map =
    locale === 'en'
      ? DECISION_TYPE_LABELS_STATIC_EN
      : DECISION_TYPE_LABELS_STATIC_SK;
  return map[typeId] ?? typeId;
}
